// Copy trading - traders, performance data, recent trades

// Generate a realistic-looking equity curve given total return %
const genEquity = (start, totalReturnPct, points = 180, vol = 0.012) => {
  const end = start * (1 + totalReturnPct / 100);
  const out = [start];
  for (let i = 1; i < points; i++) {
    const linearProgress = i / (points - 1);
    const trendValue = start + (end - start) * linearProgress;
    const noise = (Math.random() - 0.5) * trendValue * vol;
    const prev = out[i - 1];
    // Blend: bias toward trend with noise and slight momentum
    const blended = prev * 0.55 + (trendValue + noise) * 0.45;
    out.push(Math.max(start * 0.85, blended));
  }
  out[out.length - 1] = end;
  return out.map((v) => Math.round(v * 100) / 100);
};

export const traders = [
  {
    id: "oringo-ai",
    name: "Oringo AI",
    handle: "@oringo_ai",
    avatar: "OA",
    color: "#6d54ff",
    accent: "#1c3df5",
    type: "AI Bot",
    badge: "AI",
    risk: "High",
    riskScore: 8.2,
    bio: "Multi-asset neural network trained on 14 years of market data. Focuses on momentum breakouts across forex, indices and crypto. Operates 24/7 with no overnight position cap.",
    totalReturn: 1840,
    monthlyReturn: 200,
    winRate: 72,
    profitFactor: 2.8,
    maxDrawdown: 14.6,
    avgTrade: 28.4,
    totalTrades: 1842,
    followers: 12847,
    aum: 4280000,
    sharpe: 2.41,
    age: "14 months",
    minCopy: 10,
    assets: { Forex: 35, Crypto: 28, Indices: 22, Commodities: 15 },
    recentTrades: [
      { pair: "EURUSD", type: "BUY", pnl: 184.5, time: "12 min ago" },
      { pair: "BTCUSD", type: "SELL", pnl: 612.2, time: "1h ago" },
      { pair: "NAS100", type: "BUY", pnl: 295.1, time: "3h ago" },
      { pair: "XAUUSD", type: "BUY", pnl: -42.0, time: "5h ago" },
      { pair: "GBPUSD", type: "SELL", pnl: 128.7, time: "8h ago" },
      { pair: "ETHUSD", type: "BUY", pnl: 88.4, time: "11h ago" },
    ],
  },
  {
    id: "cryptoninja-ai",
    name: "CryptoNinja AI",
    handle: "@cryptoninja",
    avatar: "CN",
    color: "#f97316",
    accent: "#ea580c",
    type: "AI Bot",
    badge: "AI",
    risk: "High",
    riskScore: 8.7,
    bio: "Pure crypto play. Trades BTC, ETH, SOL with a custom momentum + sentiment model. Aggressive on breakouts, no leverage above 5x.",
    totalReturn: 1240,
    monthlyReturn: 150,
    winRate: 64,
    profitFactor: 2.15,
    maxDrawdown: 22.4,
    avgTrade: 41.2,
    totalTrades: 892,
    followers: 7421,
    aum: 1850000,
    sharpe: 1.84,
    age: "10 months",
    minCopy: 10,
    assets: { Crypto: 92, Forex: 8 },
    recentTrades: [
      { pair: "BTCUSD", type: "BUY", pnl: 412.0, time: "8 min ago" },
      { pair: "ETHUSD", type: "BUY", pnl: 156.4, time: "47 min ago" },
      { pair: "SOLUSD", type: "SELL", pnl: -88.1, time: "2h ago" },
      { pair: "BTCUSD", type: "SELL", pnl: 224.6, time: "4h ago" },
      { pair: "ETHUSD", type: "BUY", pnl: 98.2, time: "7h ago" },
    ],
  },
  {
    id: "goldrush-pro",
    name: "GoldRush Pro",
    handle: "@goldrush",
    avatar: "GR",
    color: "#f5b800",
    accent: "#d99800",
    type: "Human",
    badge: "VERIFIED",
    risk: "Medium",
    riskScore: 5.8,
    bio: "Specialist in precious metals and energy commodities. 11 years on the floor. Discretionary entries supported by macroeconomic analysis.",
    totalReturn: 820,
    monthlyReturn: 80,
    winRate: 68,
    profitFactor: 2.05,
    maxDrawdown: 11.2,
    avgTrade: 36.8,
    totalTrades: 612,
    followers: 5284,
    aum: 2140000,
    sharpe: 2.18,
    age: "16 months",
    minCopy: 10,
    assets: { Commodities: 70, Indices: 18, Forex: 12 },
    recentTrades: [
      { pair: "XAUUSD", type: "BUY", pnl: 318.0, time: "32 min ago" },
      { pair: "XAGUSD", type: "BUY", pnl: 72.4, time: "2h ago" },
      { pair: "USOIL", type: "SELL", pnl: 145.6, time: "5h ago" },
      { pair: "XAUUSD", type: "BUY", pnl: -28.0, time: "1d ago" },
      { pair: "COPPER", type: "BUY", pnl: 92.1, time: "1d ago" },
    ],
  },
  {
    id: "flippa-bot",
    name: "Flippa Bot",
    handle: "@flippa",
    avatar: "FB",
    color: "#22c55e",
    accent: "#15803d",
    type: "AI Bot",
    badge: "AI",
    risk: "Medium",
    riskScore: 6.0,
    bio: "Swing-trading bot focused on indices and gold. Holds positions 1-5 days. Tight risk controls with a 2% per-trade cap.",
    totalReturn: 380,
    monthlyReturn: 60,
    winRate: 71,
    profitFactor: 1.92,
    maxDrawdown: 7.8,
    avgTrade: 22.5,
    totalTrades: 348,
    followers: 3947,
    aum: 980000,
    sharpe: 2.06,
    age: "8 months",
    minCopy: 10,
    assets: { Indices: 48, Commodities: 32, Forex: 20 },
    recentTrades: [
      { pair: "US30", type: "BUY", pnl: 88.4, time: "1h ago" },
      { pair: "NAS100", type: "BUY", pnl: 142.2, time: "6h ago" },
      { pair: "XAUUSD", type: "BUY", pnl: 64.8, time: "1d ago" },
      { pair: "DAX40", type: "SELL", pnl: -22.0, time: "2d ago" },
      { pair: "UK100", type: "BUY", pnl: 41.5, time: "3d ago" },
    ],
  },
  {
    id: "steadyeddie",
    name: "SteadyEddie",
    handle: "@steadyeddie",
    avatar: "SE",
    color: "#38bdf8",
    accent: "#0284c7",
    type: "Human",
    badge: "VERIFIED",
    risk: "Low",
    riskScore: 2.8,
    bio: "Conservative forex carry strategy on majors. Long-term holds, low frequency, ultra-low drawdown. Built for slow, steady compounding.",
    totalReturn: 420,
    monthlyReturn: 30,
    winRate: 81,
    profitFactor: 2.46,
    maxDrawdown: 3.4,
    avgTrade: 14.2,
    totalTrades: 208,
    followers: 8120,
    aum: 3450000,
    sharpe: 3.12,
    age: "22 months",
    minCopy: 10,
    assets: { Forex: 100 },
    recentTrades: [
      { pair: "AUDJPY", type: "BUY", pnl: 28.4, time: "4h ago" },
      { pair: "NZDJPY", type: "BUY", pnl: 18.2, time: "1d ago" },
      { pair: "EURUSD", type: "BUY", pnl: 22.5, time: "2d ago" },
      { pair: "GBPUSD", type: "SELL", pnl: 14.1, time: "4d ago" },
      { pair: "USDCAD", type: "BUY", pnl: 9.8, time: "6d ago" },
    ],
  },
];

// Attach equity curves
traders.forEach((t) => {
  t.equity = genEquity(10000, t.totalReturn, 180, 0.008 + (t.riskScore / 1000));
});

export const getTrader = (id) => traders.find((t) => t.id === id);

export const riskBadge = (risk) => {
  if (risk === "Low") return { color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/25" };
  if (risk === "Medium") return { color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/25" };
  return { color: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/25" };
};
